/**
 * Pure helpers for AriaCashForecastCard — unit-tested without a React harness.
 */
import type { AriaCashForecastDigest, AriaCashForecastWeek } from '../../api/ariaChat'

export function showBillsOutColumn(weeks: AriaCashForecastWeek[]): boolean {
  return weeks.some((w) => (w.other_out?.amount || 0) !== 0)
}

export function showOverheadColumn(weeks: AriaCashForecastWeek[]): boolean {
  return weeks.some((w) => (w.overhead?.amount || 0) !== 0)
}

export function verdictLine(verdict: AriaCashForecastDigest['verdict']): string {
  if (!verdict || verdict.status === 'unknown') {
    return 'Need bank balance to judge cash position.'
  }
  const lowest = verdict.lowest_line
  if (verdict.status === 'positive') {
    return lowest
      ? `Cash positive across the window. ${lowest}.`
      : 'Cash positive across the window.'
  }
  const week = verdict.first_short_week || 'an upcoming week'
  const short = verdict.shortfall?.formatted
  const shortPart = short
    ? `Short starting week of ${week} — shortfall ${short}.`
    : `Short starting week of ${week}.`
  return lowest ? `${shortPart} ${lowest}.` : shortPart
}

export function isShortWeek(
  weekStart: string,
  verdict: AriaCashForecastDigest['verdict'],
): boolean {
  return !!verdict?.first_short_week && verdict.first_short_week === weekStart
}

export function followUpButtonLabel(collectNowLen: number): string {
  if (collectNowLen <= 0) return ''
  const n = Math.min(collectNowLen, 3)
  const more = collectNowLen > 3 ? collectNowLen - 3 : 0
  const base = `Draft follow-ups on ${n} overdue Pay App${n === 1 ? '' : 's'}`
  return more > 0 ? `${base} (and ${more} more)` : base
}

export function takeFollowUpIds(
  collectNow: { pay_app_id?: number | null }[],
  max = 3,
): { ids: number[]; more: number } {
  const ids = collectNow
    .map((r) => (r.pay_app_id != null ? Number(r.pay_app_id) : NaN))
    .filter((id) => Number.isFinite(id) && id > 0)
  return { ids: ids.slice(0, max), more: Math.max(0, ids.length - max) }
}

export function formatWeekLabel(iso: string): string {
  // Keep ISO date short: YYYY-MM-DD → Mon DD
  const d = new Date(`${iso}T12:00:00`)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}
